import UUID from 'uuid-js';
import Enum from '@square/dashboard-utilities/utils/enum';
import ISO8601 from '@square/dashboard-data/models/squareup/bills/iso8601-date';
import MoneyProto from '@square/dashboard-data/models/squareup/common/money-proto';
import TokenPair from '@square/dashboard-data/models/squareup/common/token/token-pair';
import { ProtoBaseModel, attr, repeated } from '@square/dashboard-data/utils/ember-model-stubs';
import { ApplicationScope, CalculationPhase, CatalogObjectType, InclusionType, TranslationType } from '@square/dashboard-items-data/models/invoices/enums';
import FeeSnapshot from '@square/dashboard-items-data/models/invoices/cart/fee-snapshot';

// squareup.bills.FeeLineItem.Amounts
const Amounts = ProtoBaseModel.extend({
  afterApplicationTotalMoney: attr(MoneyProto),
  appliedMoney: attr(MoneyProto),
  appliedToMoney: attr(MoneyProto)
});

// squareup.bills.FeeLineItem.ApplicationMethod
export const ApplicationMethod = new Enum(['UNKNOWN', 'MANUAL', 'AUTOMATIC']);

// squareup.items.merchant.Attribute
const Attribute = ProtoBaseModel.extend({
  definitionToken: attr(String),
  intValue: attr(Number),
  stringValue: attr(String),
  unitToken: attr(String)
});

// squareup.bills.FeeLineItem.BackingDetails.BackingType
const BackingType = new Enum(['ITEMS_SERVICE_FEE', 'CUSTOM_FEE']);

// squareup.items.merchant.CatalogObject
const CatalogObject = ProtoBaseModel.extend({
  attribute: repeated(Attribute),
  cogsId: attr(String),
  deleted: attr(Boolean),
  token: attr(String),
  type: attr(CatalogObjectType),
  version: attr(Number)
});

// squareup.bills.FeeLineItem.BackingDetails.MerchantCatalogObjects
const MerchantCatalogObjects = ProtoBaseModel.extend({
  fee: attr(CatalogObject)
});

// squareup.bills.FeeLineItem.BackingDetails
const BackingDetails = ProtoBaseModel.extend({
  backingType: attr(BackingType),
  fee: attr(FeeSnapshot),
  merchantCatalogObjects: attr(MerchantCatalogObjects)
});

// squareup.bills.FeeLineItem.DisplayDetails
const DisplayDetails = ProtoBaseModel.extend({
  calculationPhase: attr(CalculationPhase),
  cogsObjectId: attr(String),
  inclusionType: attr(InclusionType),
  name: attr(String),
  percentage: attr(String),
  taxLabelOrder: attr(Number),
  taxTypeId: attr(String),
  taxTypeName: attr(String),
  taxTypeNumber: attr(String)
});

// squareup.bills.TranslatedName
const TranslatedName = ProtoBaseModel.extend({
  localizedName: attr(String),
  translationType: attr(TranslationType)
});

// squareup.bills.DatePair
const DatePair = ProtoBaseModel.extend({
  clientDate: attr(ISO8601),
  serverDate: attr(ISO8601)
});

// squareup.bills.FeeLineItem
const FeeLineItem = ProtoBaseModel.extend({
  amounts: attr(Amounts),
  applicationMethod: attr(ApplicationMethod, {
    defaultValue: ApplicationMethod.MANUAL
  }),
  applicationScope: attr(ApplicationScope),
  createdAt: attr(DatePair),
  feeLineItemTokenPair: attr(TokenPair),
  readOnlyDisplayDetails: attr(DisplayDetails),
  readOnlyTranslatedName: attr(TranslatedName),
  writeOnlyBackingDetails: attr(BackingDetails),
  clone() {
    const feeLineItemClone = FeeLineItem.deserialize(FeeLineItem.serialize(this));
    feeLineItemClone.set('feeLineItemTokenPair', TokenPair.create({
      clientToken: UUID.create().toString()
    }));
    return feeLineItemClone;
  }
}).reopenClass({
  Amounts,
  BackingDetails,
  createFor(fee) {
    return this.create({
      feeLineItemTokenPair: TokenPair.create({
        clientToken: UUID.create().toString()
      }),
      writeOnlyBackingDetails: BackingDetails.create({
        fee: FeeSnapshot.createFor(fee)
      })
    });
  }
});
export default FeeLineItem;